import { useApp } from '@hb/react'
import {
  ContactBillingStatus,
  ContactType,
  FollowUpAction,
  logContactField,
  TelephoneContact,
  WithId,
} from '@hb/shared'
import { useCallback, useMemo } from 'react'
import { saveContactLog } from '../../../backend/functions/gyn'
import { GenericEditModal } from '../../../components/DataView/GenericEditModal'
import { useGynCareView } from '../../../components/Gyn/context'

interface Props {
  assessmentId: string
  patientId: string
  isOpen: boolean
  onClose: () => void
  /** When provided, modal opens in edit mode pre-populated with the existing contact. */
  editContact?: WithId<TelephoneContact>
}

export const LogContactModal = ({
  assessmentId,
  patientId,
  isOpen,
  onClose,
  editContact,
}: Props) => {
  const { appName } = useApp()
  const { practiceId } = useGynCareView()

  const initialData = useMemo(() => {
    if (editContact) {
      return {
        date: editContact.date,
        contactType: editContact.contactType,
        ...(editContact.durationMinutes != null
          ? { durationMinutes: editContact.durationMinutes }
          : {}),
        clinicalSummary: editContact.clinicalSummary,
        followUpAction: editContact.followUpAction,
        ...(editContact.followUpActionDetail
          ? { followUpActionDetail: editContact.followUpActionDetail }
          : {}),
        billingStatus: editContact.billingStatus,
      }
    }
    return {
      date: new Date().toISOString(),
      billingStatus: 'do-not-bill' as ContactBillingStatus,
    }
    // recalculate when modal opens or editContact identity changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editContact?.id])

  const handleSubmit = useCallback(
    async (data: Record<string, any>) => {
      if (!practiceId) return { error: 'Practice not found' }
      try {
        await saveContactLog({
          contactId: editContact?.id ?? null,
          assessmentId,
          patientId,
          practiceId,
          date: data.date as string,
          contactType: data.contactType as ContactType,
          ...(data.durationMinutes != null
            ? { durationMinutes: data.durationMinutes as number }
            : {}),
          clinicalSummary: (data.clinicalSummary as string).trim(),
          followUpAction: data.followUpAction as FollowUpAction,
          ...(data.followUpActionDetail
            ? { followUpActionDetail: data.followUpActionDetail as string }
            : {}),
          billingStatus: (data.billingStatus ?? 'do-not-bill') as ContactBillingStatus,
          appName,
        })
        return { success: editContact ? 'Contact updated' : 'Contact logged' }
      } catch {
        return { error: editContact ? 'Failed to update contact' : 'Failed to log contact' }
      }
    },
    [practiceId, assessmentId, patientId, appName, editContact],
  )

  return (
    <GenericEditModal
      isOpen={isOpen}
      onClose={onClose}
      field={logContactField}
      data={initialData}
      onSubmit={handleSubmit}
      subheader="Document a telephone contact, message, or brief clinical interaction."
      submitText={editContact ? 'Save Changes' : 'Log Contact'}
    />
  )
}
